import { PartnershipOpportunity } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import { SNAPSHOT_TOP_INTERESTS_PER_DIMENSION } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { CreatorSetMemberInsightsSnapshot } from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';

// The open set's id, as a query param so a set can be linked to directly.
export const SET_PARAM = 'set';

// Mixpanel `entity` for every Creator Set record event.
export const CREATOR_SET_ENTITY = 'creator set';

// A channel lookup returns full profiles (~76 KB each for creators who share viewer data).
// getCreatorInsights returns at most 100 creators and drops any past a 15 MB budget, gzipping large
// responses, so 100 channels (~7.6 MB raw) fit in one call. Lookups (Refresh, and saving from
// Discover) ask in batches that size, one at a time (the service allows 5 requests per second).
export const CHANNEL_LOOKUP_BATCH_SIZE = 100;

// One member's metrics before and after "Refresh metrics". `next` is undefined when Google didn't
// return the channel, in which case the member keeps its old snapshot.
export interface MemberMetricsChange {
  channelId: string;
  displayName: string;
  previous: CreatorSetMemberInsightsSnapshot;
  next?: CreatorSetMemberInsightsSnapshot;
}

// The `dimension` values a member snapshot's audience attributes carry. Others are ignored.
export const AudienceDimension = {
  AGE_RANGE: 'AGE_RANGE',
  GENDER: 'GENDER',
  DEVICE: 'DEVICE',
  GEO_TARGET_COUNTRY: 'GEO_TARGET_COUNTRY',
  KNOWLEDGE_GRAPH: 'KNOWLEDGE_GRAPH',
  AFFINITY_USER_INTEREST: 'AFFINITY_USER_INTEREST',
  IN_MARKET_USER_INTEREST: 'IN_MARKET_USER_INTEREST'
} as const;
export type AudienceDimensionValue = (typeof AudienceDimension)[keyof typeof AudienceDimension];

// An age bucket joins the suggested Age Range when at least this share of the set's age-known
// viewers fall in it. Build Recommendation needs one contiguous span, so buckets between two that
// qualify join too.
export const AGE_SHARE_MIN = 0.1;
// One gender is suggested only when it has at least this share of the set's gender-known viewers;
// otherwise All Genders.
export const GENDER_DOMINANCE_MIN = 0.6;
// Reference-only rows show this many of the set's top devices, countries and other interests.
export const REFERENCE_ROW_LIMIT = 3;

export const AttributeField = {
  INTEREST: 'Audience Interest',
  GENDER: 'Gender',
  AGE_RANGE: 'Age Range'
} as const;
export type AttributeFieldValue = (typeof AttributeField)[keyof typeof AttributeField];

// One Build Recommendation field's suggested value, and the caveat shown beside it.
export interface CopyableRow {
  label: AttributeFieldValue;
  value?: string;
  note?: string;
}

// How members are weighted in the aggregate. Equal when no member has a subscriber count.
export const Weighting = {
  SUBSCRIBERS: 'subscribers',
  EQUAL: 'equal'
} as const;
export type WeightingValue = (typeof Weighting)[keyof typeof Weighting];

// Mixpanel `action` for exporting a set to Excel.
export const EXPORT_ACTION = 'export creator set';

// Mixpanel `action` for opening Build Recommendation with a set's suggested inputs filled in.
export const USE_IN_BUILD_ACTION = 'use in build recommendation';

// The export's yes/no partnership columns, by Google's partnership type. PARTNERSHIP_ADS is left
// out: every creator has it.
export const EXPORT_PARTNERSHIP_COLUMNS = [
  { type: PartnershipOpportunity.CREATOR_PARTNERSHIPS, label: 'BrandConnect' },
  { type: PartnershipOpportunity.YOUTUBE_SELECT_LINEUPS, label: 'YouTube Select' },
  { type: PartnershipOpportunity.CREATOR_TAKEOVER, label: 'Creator Takeover' }
] as const;

// What a snapshot keeps of a creator's audience (see toCreatorSetMemberSnapshot), stated in the
// export so a reader knows why an interest they expected is missing.
export const EXPORT_AUDIENCE_NOTE =
  'Every age, gender, device and country share; the top 25 topics, affinity and in-market interests';

export const OTHER_INTERESTS_TOOLTIP =
  'Other interests include affinity and in-market interests. These interests are not available ' +
  'in the Audience Interest search, so they cannot be used to build recommendations.';

export const SHARES_VIEWER_DATA_TOOLTIP =
  'Shows if the creator shares viewer data with Google. Only these creators count toward the ' +
  'Build Recommendation inputs.';

// The set detail's creator comparison, built from the stored snapshots.
export const SnapshotView = {
  INTERESTS: 'interests',
  DEMOGRAPHICS: 'demographics',
  TOP_VIDEOS: 'top-videos'
} as const;
export type SnapshotViewValue = (typeof SnapshotView)[keyof typeof SnapshotView];

export const SNAPSHOT_VIEW_OPTIONS: { key: SnapshotViewValue; label: string }[] = [
  { key: SnapshotView.INTERESTS, label: 'Interests' },
  { key: SnapshotView.DEMOGRAPHICS, label: 'Demographics' },
  { key: SnapshotView.TOP_VIDEOS, label: 'Top videos' }
];

export const INTEREST_DIMENSIONS = [
  AudienceDimension.KNOWLEDGE_GRAPH,
  AudienceDimension.AFFINITY_USER_INTEREST,
  AudienceDimension.IN_MARKET_USER_INTEREST
] as const;
export type InterestDimension = (typeof INTEREST_DIMENSIONS)[number];

export const DEMOGRAPHIC_DIMENSIONS = [
  AudienceDimension.GENDER,
  AudienceDimension.AGE_RANGE,
  AudienceDimension.DEVICE,
  AudienceDimension.GEO_TARGET_COUNTRY
] as const;
export type DemographicDimension = (typeof DEMOGRAPHIC_DIMENSIONS)[number];

export const DEMOGRAPHIC_LABELS: Record<DemographicDimension, string> = {
  [AudienceDimension.GENDER]: 'Gender',
  [AudienceDimension.AGE_RANGE]: 'Age',
  [AudienceDimension.DEVICE]: 'Device',
  [AudienceDimension.GEO_TARGET_COUNTRY]: 'Country'
};

export const INTEREST_TYPE_LABELS: Record<InterestDimension, string> = {
  [AudienceDimension.KNOWLEDGE_GRAPH]: 'Topic',
  [AudienceDimension.AFFINITY_USER_INTEREST]: 'Affinity',
  [AudienceDimension.IN_MARKET_USER_INTEREST]: 'In-market'
};

export const ALL_INTEREST_TYPES = 'all';
export type InterestTypeFilter = InterestDimension | typeof ALL_INTEREST_TYPES;

const INTEREST_TYPE_TOOLTIPS: Record<InterestDimension, string> = {
  [AudienceDimension.KNOWLEDGE_GRAPH]:
    'Subjects the creator’s viewers are interested in, such as Yoga or Beauty Tips.',
  [AudienceDimension.AFFINITY_USER_INTEREST]:
    'Long-term passions and habits of the creator’s viewers, such as Health & Fitness Buffs. ' +
    'Useful for brand awareness.',
  [AudienceDimension.IN_MARKET_USER_INTEREST]:
    'Products and services the creator’s viewers are researching or plan to buy soon, such as ' +
    'Fitness Products & Services. Useful for purchase intent.'
};

export const INTEREST_TYPE_OPTIONS: { key: InterestTypeFilter; label: string; tooltip: string }[] =
  [
    { key: ALL_INTEREST_TYPES, label: 'All', tooltip: 'Topic, affinity and in-market interests.' },
    ...INTEREST_DIMENSIONS.map(key => ({
      key,
      label: INTEREST_TYPE_LABELS[key],
      tooltip: INTEREST_TYPE_TOOLTIPS[key]
    }))
  ];

export const InterestSort = {
  DISTINCTIVE: 'distinctive',
  SHARE: 'share'
} as const;
export type InterestSortValue = (typeof InterestSort)[keyof typeof InterestSort];

export const INTEREST_SORT_OPTIONS: { key: InterestSortValue; label: string; tooltip: string }[] = [
  {
    key: InterestSort.DISTINCTIVE,
    label: 'Most distinctive',
    tooltip: 'Shows first the interests where one creator’s viewers lead the others by the most.'
  },
  {
    key: InterestSort.SHARE,
    label: 'Highest share',
    tooltip: 'Shows first the interests with the highest viewer share for any creator.'
  }
];

// With two or more compared creators, an interest is broad when at least this share of each one's
// viewers have it, e.g. Lifestyle or Shoppers.
export const BROAD_SHARE_MIN = 0.8;
// A creator leads an interest when their viewer share is at least this much above every other
// compared creator's.
export const LEAD_MIN = 0.1;
// How many interests a creator's summary lists under "Stands out for".
export const STANDOUT_LIMIT = 3;

export const BROAD_INTERESTS_TOOLTIP =
  `Hides interests that at least ${BROAD_SHARE_MIN * 100}% of every creator’s viewers have, ` +
  'such as Lifestyle or Shoppers. They are true of almost any audience, so they don’t help you ' +
  'choose between creators.';

// How each demographic group's shares add up, as Google reports them.
export const DEMOGRAPHIC_SHARE_NOTES: Record<DemographicDimension, string> = {
  [AudienceDimension.GENDER]:
    'Google doesn’t know every viewer’s gender, so Female + Male can be under 100%.',
  [AudienceDimension.AGE_RANGE]:
    'Each viewer is in one age group, so the groups add up to about 100%.',
  [AudienceDimension.DEVICE]:
    'Viewers who watch on that device. Many use more than one, so the total is over 100%.',
  [AudienceDimension.GEO_TARGET_COUNTRY]:
    'Only the top countries Google reports show, so the total is under 100%.'
};

export const NO_VIEWER_DATA =
  'None of these creators share viewer data with Google, so there are no audience details to compare.';

export const INTEREST_SHARES_NOTE =
  'Percent of each creator’s viewers with the interest. A snapshot keeps each creator’s top ' +
  `${SNAPSHOT_TOP_INTERESTS_PER_DIMENSION} interests of each type. “—” means the interest isn’t ` +
  'in that list, so the share is under the creator’s lowest kept share (hover to see it). ' +
  `Highlighted cells lead the other creators by ${LEAD_MIN * 100} points or more.`;
